import {
    AfterViewInit,
    Component,
    HostListener,
    Inject,
    OnDestroy,
    PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * Botón flotante global que:
 *   - Muestra "↑" (scroll to top) cuando scrolleaste > 300px desde el top.
 *   - Muestra "↓" (scroll to bottom) cuando NO estás cerca del bottom.
 *   - Click → scroll suave hasta el extremo.
 *   - Se posiciona a la derecha, encima del WhatsApp button.
 *   - Respeta prefers-reduced-motion (scroll instantáneo en vez de suave).
 *   - SSR-safe.
 */
@Component({
    selector: 'app-scroll-buttons',
    templateUrl: './scroll-buttons.component.html',
    styleUrls: ['./scroll-buttons.component.scss'],
})
export class ScrollButtonsComponent implements AfterViewInit, OnDestroy {
    /** Mostrar botón de "scroll to top" (cuando hay scroll hacia arriba que hacer) */
    showTopButton = false;

    /** Mostrar botón de "scroll to bottom" (cuando NO estás cerca del bottom) */
    showBottomButton = true;

    private prefersReducedMotion = false;

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: object,
        @Inject(DOCUMENT) private readonly doc: Document,
    ) {}

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        try {
            this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch { /* ignore */ }
        // Calcular estado inicial en cuanto el DOM esté listo
        setTimeout(() => this.updateVisibility(), 0);
    }

    ngOnDestroy(): void { /* nada que limpiar — HostListener se desuscribe solo */ }

    @HostListener('window:scroll')
    onScroll(): void {
        this.updateVisibility();
    }

    @HostListener('window:resize')
    onResize(): void {
        this.updateVisibility();
    }

    private updateVisibility(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        const scrollY = window.scrollY || this.doc.documentElement.scrollTop || 0;
        const viewportH = window.innerHeight;
        const pageH = Math.max(
            this.doc.documentElement.scrollHeight,
            this.doc.body?.scrollHeight || 0,
        );

        // Scroll to top: visible si ya scrolleaste > 300px
        this.showTopButton = scrollY > 300;

        // Scroll to bottom: visible si todavía falta scroll y la página es lo
        // suficientemente larga (>1.5 viewports) — para evitar mostrarlo en
        // páginas cortas donde no hay nada hacia donde bajar.
        const distanceToBottom = pageH - (scrollY + viewportH);
        const pageIsLong = pageH > viewportH * 1.5;
        this.showBottomButton = pageIsLong && distanceToBottom > 200;
    }

    scrollToTop(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        window.scrollTo({
            top: 0,
            behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
    }

    scrollToBottom(): void {
        if (!isPlatformBrowser(this.platformId)) return;
        const pageH = Math.max(
            this.doc.documentElement.scrollHeight,
            this.doc.body?.scrollHeight || 0,
        );
        window.scrollTo({
            top: pageH,
            behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
    }
}
